import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import BusyOverlay from 'components/common/BusyOverlay'
import { LinkButton } from 'components/common/Button'
import Text from 'components/common/Text'
import CardWrapper from 'components/dashboard/CardWrapper'
import Icon from 'components/Icon'
import SvgStatusCheck from 'components/icons/StatusCheck'
import SvgWarning from 'components/icons/Warning'
import useCompanyReportData from 'hooks/use-company-report-data'
import theme from 'theme'

import EmptyCard from './EmptyCard'

function NotificationMessage({ remainingHostsToAttack }) {
  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        flexDirection: 'column',
        width: 200
      }}
    >
      {remainingHostsToAttack > 0 ? (
        <SvgWarning
          css={{
            height: theme.space[5],
            width: theme.space[5],
            margin: theme.space[2],
            marginTop: -2,
            color: theme.colors.orange[0]
          }}
        />
      ) : (
        <SvgStatusCheck
          css={{
            fontSize: theme.space[5],
            marginTop: -2,
            color: theme.colors.green[0]
          }}
        />
      )}
      {remainingHostsToAttack > 0 ? (
        <Text
          as="p"
          css={{
            fontSize: theme.fontSizes[0]
          }}
        >
          There are <strong>{remainingHostsToAttack} hosts</strong> we recommend
          you attack next.
        </Text>
      ) : (
        <Text
          as="p"
          css={{
            fontSize: theme.fontSizes[0]
          }}
        >
          Great job attacking all of your hosts
        </Text>
      )}
      <Text
        as={Link}
        css={{
          textDecoration: 'underline',
          fontSize: theme.fontSizes[0]
        }}
        to="/schedules"
      >
        Schedule attacks
      </Text>
    </div>
  )
}

NotificationMessage.propTypes = { remainingHostsToAttack: PropTypes.number }

function StatTile({ attackedHosts, activeClients, attackedHostsLastMonth }) {
  const didHostAttackedIncrease = attackedHosts > attackedHostsLastMonth

  return (
    <div
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        flexDirection: 'column'
      }}
    >
      <Text as="h1" color="medium">
        {attackedHosts} of {activeClients}
      </Text>
      <Text
        as="h6"
        css={{
          textTransform: 'uppercase',
          fontSize: theme.fontSizes[1],
          fontWeight: theme.fontWeights[1]
        }}
      >
        Targets Attacked
      </Text>
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center'
        }}
      >
        {didHostAttackedIncrease ? (
          <Icon
            css={{ color: theme.colors.green[0], fontSize: theme.fontSizes[3] }}
            icon="trendingUp"
          />
        ) : (
          <Icon
            css={{ color: theme.colors.red[0], fontSize: theme.fontSizes[3] }}
            icon="trendingDown"
          />
        )}

        <Text
          as="small"
          css={{
            marginLeft: theme.space[2],
            color: theme.colors.steel[1],
            fontWeight: theme.fontWeights[1]
          }}
        >
          Last Month:
        </Text>
        <Text
          as="h4"
          color="medium"
          css={{
            marginLeft: theme.space[1],
            color: theme.colors.steel[1],
            fontSize: theme.fontSizes[1],
            marginTop: -2
          }}
        >
          {attackedHostsLastMonth}
        </Text>
      </div>
    </div>
  )
}

StatTile.propTypes = {
  attackedHosts: PropTypes.number,
  activeClients: PropTypes.number,
  attackedHostsLastMonth: PropTypes.number
}

function UniqueTargetsAttacked() {
  const { activeClients, attackedHosts, attackedHostsLastMonth, isLoading } =
    useCompanyReportData({ skipCompanyDetailsRequest: true })

  // TODO: uncomment once the data are available
  // const graphValues = useFormik({
  //   initialValues: {
  //     targetingStrategy: 'Exact',
  //     selectedTargets: [],
  //     targetParams: {}
  //   }
  // })

  if (isLoading) return <BusyOverlay rounded shown />

  if (activeClients.length === 0) {
    return (
      <EmptyCard title="No Attacks Run">
        <LinkButton label="Run an attack" to="/attacks/new/" />
      </EmptyCard>
    )
  }

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        alignItems: 'center',
        height: '100%',
        padding: theme.space[5],
        gap: theme.space[4]
      }}
    >
      <div>
        {/* Uncomment once the data are available */}
        {/* <InfraTargetsGraph
            orientation="left"
            targets={allClients}
            targetType="Hosts"
          /> */}
        <StatTile
          activeClients={activeClients}
          attackedHosts={attackedHosts}
          attackedHostsLastMonth={attackedHostsLastMonth}
        />
      </div>
      <div
        css={{
          display: 'grid',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <NotificationMessage
          remainingHostsToAttack={activeClients - attackedHosts}
        />
      </div>
    </div>
  )
}

export default function UniqueTargetsAttackedCard() {
  return (
    <CardWrapper
      hideFilter={true}
      renderCardContent={() => <UniqueTargetsAttacked />}
      title="Unique Targets Attacked"
    />
  )
}

UniqueTargetsAttackedCard.propTypes = {
  infraClients: PropTypes.object,
  alfiClients: PropTypes.object,
  isLoading: PropTypes.bool,
  completedAttacks: PropTypes.array,
  activeAttacks: PropTypes.array
}
